import { forwardRef, useImperativeHandle, useRef } from 'react';
import InputError from './InputError';
import styles from './TextInput.scss';

interface TextInputProps extends React.HTMLProps<HTMLInputElement> {
  /** current email value */
  value: string;
  handleChange: ({ value, isValid }: { value: string; isValid: boolean }) => void;
  onBlur?: () => void;
  placeholder?: string;
  /** text of the error, ignored if empty string */
  error?: string | boolean;
  className?: string;
  /** element to be placed at the left end of the input */
  leftSideSlot?: React.ReactNode;
  /** element to be placed at the right end of the input */
  rightSideSlot?: React.ReactNode;
  /** Hide error view, removes internal bottom padding */
  hideError?: boolean;
}

/**
 * Text input according to the design system.
 * https://www.figma.com/file/cKU9Z0zAvOHkVJNCoWlSCs/%F0%9F%96%A5-Clear-DS-(Web-Kit)?node-id=11%3A10412
 */
const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      value,
      placeholder,
      handleChange,
      onBlur,
      error,
      className = '',
      leftSideSlot = null,
      rightSideSlot = null,
      hideError = false,
      ...props
    },
    externalInputRef,
  ) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- safe to assume, because of the ref
    useImperativeHandle(externalInputRef, () => inputRef.current!, []);

    const onChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
      handleChange({
        value: event.target.value,
        isValid: inputRef.current?.checkValidity() ?? false,
      });
    };

    const handleBlur = () => {
      // avoid calling onBlur, when focus switches to element within wrapper
      // i.e. prevent validation callback, when side button within input is clicked
      if (onBlur && !wrapperRef.current?.matches(':focus-within')) {
        onBlur();
      }
    };

    return (
      <div className={`${styles.field} ${className || ''}`} ref={wrapperRef}>
        <div className={`${styles.wrapper} ${error ? styles.errorWrapper : ''}`}>
          {leftSideSlot}

          <input
            ref={inputRef}
            type="text"
            placeholder={placeholder}
            value={value}
            className={styles.input}
            onChange={onChange}
            onBlur={handleBlur}
            {...props}
          />

          {rightSideSlot}
        </div>

        {!hideError && <InputError error={error} />}
      </div>
    );
  },
);

TextInput.displayName = 'TextInput';

export default TextInput;
